import { createFileRoute, Link, useLoaderData } from "@tanstack/react-router";
import { useEffect } from "react";
import { Check, AlertTriangle, Mail } from "lucide-react";
import { track } from "@/lib/track";
import { confirmSubscription } from "@/lib/subscribers.functions";

type LoaderResult =
  | { state: "confirmed"; email: string }
  | { state: "already"; email: string }
  | { state: "invalid" };

export const Route = createFileRoute("/confirm-subscription")({
  head: () => ({
    meta: [
      { title: "Confirm your subscription — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Confirm your email address to start receiving Recovery Trauma™ notes.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    email: typeof s.email === "string" ? s.email : "",
    token: typeof s.token === "string" ? s.token : "",
  }),
  // Runs server-side on the first GET so the link works from any mail client,
  // with no client-side JavaScript required.
  loader: async ({ location }): Promise<LoaderResult> => {
    const search = location.search as { email?: string; token?: string };
    const email = (search.email ?? "").trim();
    const token = (search.token ?? "").trim();
    if (!email || !token) return { state: "invalid" };
    try {
      const res = await confirmSubscription({ data: { email, token } });
      if (!res.ok) return { state: "invalid" };
      return { state: res.already ? "already" : "confirmed", email };
    } catch {
      return { state: "invalid" };
    }
  },
  component: ConfirmSubscriptionPage,
});

function ConfirmSubscriptionPage() {
  const data = useLoaderData({ from: "/confirm-subscription" }) as LoaderResult;

  useEffect(() => {
    if (data.state === "confirmed") track("newsletter_confirmed", {});
  }, [data.state]);

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-xl flex-col justify-center px-5 py-16">
      <div className="rounded-2xl border border-border bg-card p-7 shadow-sm">
        {data.state === "invalid" ? (
          <>
            <AlertTriangle className="mb-3 h-6 w-6 text-muted-foreground" />
            <h1 className="text-xl font-semibold text-foreground">
              This confirmation link isn't valid
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              It may have been copied incompletely, or the address was already
              removed. You can subscribe again from the homepage and we'll send a
              fresh confirmation email.
            </p>
          </>
        ) : (
          <>
            <Check className="mb-3 h-6 w-6 text-[var(--gold-deep)]" />
            <h1 className="text-xl font-semibold text-foreground">
              {data.state === "already"
                ? "You're already confirmed 🌱"
                : "You're confirmed 🌿"}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {data.state === "already" ? (
                <>
                  <strong className="text-foreground">{data.email}</strong> is
                  already on the list — there's nothing more to do.
                </>
              ) : (
                <>
                  Thank you. <strong className="text-foreground">{data.email}</strong>{" "}
                  is now subscribed to Recovery Trauma™ notes. A short welcome
                  email is on its way explaining exactly what to expect.
                </>
              )}
            </p>
            <p className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
              <Mail className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                A couple of emails a month at most, and you can leave at any time.
              </span>
            </p>
          </>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/explore"
            className="rounded-full bg-[var(--gold-deep)] px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            Take a look inside
          </Link>
          <Link
            to="/"
            className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground"
          >
            Back to homepage
          </Link>
        </div>
      </div>
    </main>
  );
}
